import { create } from 'zustand';
import { supabase } from '@/lib/supabase';
import { db } from '@/lib/db/db';
import type { CoachInvite, AthleteShare } from '@/types';

interface RoleState {
    // Coach specific
    sharedAthletes: any[]; // Extended athlete view for coach
    teamDashboardStats: any | null;

    // Parent specific
    sentInvites: CoachInvite[];
    activeShares: AthleteShare[];

    loading: boolean;
    error: string | null;

    // Actions
    fetchCoachData: (userId: string) => Promise<void>;
    fetchParentShares: (athleteId: string) => Promise<void>;
    createInvite: (athleteId: string, email: string, canEdit: boolean) => Promise<{ success: boolean, error?: string }>;
    revokeInvite: (inviteId: string) => Promise<void>;
    acceptInvite: (token: string, coachId: string) => Promise<{ success: boolean, error?: string }>;
}

export const useRoleStore = create<RoleState>((set, get) => ({
    sharedAthletes: [],
    teamDashboardStats: null,
    sentInvites: [],
    activeShares: [],
    loading: false,
    error: null,

    fetchCoachData: async (userId) => {
        set({ loading: true, error: null });
        try {
            // First get shares
            const { data: shares, error: shareError } = await supabase
                .from('athlete_shares')
                .select('*')
                .eq('coach_user_id', userId);

            if (shareError) throw shareError;

            // Fetch actual athletes from Dexie for now (since we use local-first mixed model)
            // In a full migration, these would come from Supabase as well.
            const sharedAthleteIds = shares?.map(s => s.athlete_id) || [];
            if (sharedAthleteIds.length > 0) {
                const athletes = await db.athletes.where('id').anyOf(sharedAthleteIds).toArray();

                // compute basic team stats
                const totalAthletes = athletes.length;
                // We'd load matches here too, keeping simple for scaffolding
                set({
                    sharedAthletes: athletes,
                    teamDashboardStats: { totalAthletes }
                });
            } else {
                set({ sharedAthletes: [], teamDashboardStats: { totalAthletes: 0 } });
            }
        } catch (e: any) {
            set({ error: e.message });
        } finally {
            set({ loading: false });
        }
    },

    fetchParentShares: async (athleteId) => {
        set({ loading: true, error: null });
        try {
            const { data: invites } = await supabase
                .from('coach_invites')
                .select('*')
                .eq('athlete_id', athleteId);

            const { data: shares } = await supabase
                .from('athlete_shares')
                .select('*')
                .eq('athlete_id', athleteId);

            set({
                sentInvites: invites || [],
                activeShares: shares || []
            });
        } catch (e: any) {
            set({ error: e.message });
        } finally {
            set({ loading: false });
        }
    },

    createInvite: async (athleteId, email, canEdit) => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('Not authenticated');

            // Generate simple token (in prod, use secure random)
            const token = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
            const expires_at = new Date();
            expires_at.setDate(expires_at.getDate() + 7); // 7 days

            const { error } = await supabase
                .from('coach_invites')
                .insert({
                    athlete_id: athleteId,
                    invited_email: email,
                    invited_by_user_id: user.id,
                    can_edit: canEdit,
                    token: token,
                    expires_at: expires_at.toISOString()
                });

            if (error) throw error;

            // Refresh
            get().fetchParentShares(athleteId);
            return { success: true };
        } catch (e: any) {
            return { success: false, error: e.message };
        }
    },

    revokeInvite: async (inviteId) => {
        try {
            await supabase.from('coach_invites').delete().eq('id', inviteId);
            set(state => ({
                sentInvites: state.sentInvites.filter(i => i.id !== inviteId)
            }));
        } catch (e) {
            console.error(e);
        }
    },

    acceptInvite: async (token, coachId) => {
        try {
            // Find invite
            const { data: invite, error } = await supabase
                .from('coach_invites')
                .select('*')
                .eq('token', token)
                .single();

            if (error || !invite) throw new Error('Invalid or expired invite token');

            if (new Date(invite.expires_at) < new Date()) {
                throw new Error('Invite expired');
            }

            if (invite.status !== 'pending') {
                throw new Error('Invite already used');
            }

            // Create share
            const { error: shareError } = await supabase
                .from('athlete_shares')
                .insert({
                    athlete_id: invite.athlete_id,
                    coach_user_id: coachId,
                    can_edit: invite.can_edit
                });

            if (shareError) throw shareError;

            // Mark accepted
            await supabase
                .from('coach_invites')
                .update({ status: 'accepted' })
                .eq('id', invite.id);

            return { success: true };
        } catch (e: any) {
            return { success: false, error: e.message };
        }
    }
}));
